import { useState, useEffect, useRef } from "react";
import { X, Image as ImageIcon, Trash2, Star, Loader2, AlertCircle, Plus, Upload, Camera } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import Swal from "sweetalert2";
import { 
  obtenerFotosZonaComun,
  eliminarFotoZonaComun,
  marcarFotoPrincipal,
  insertarFotosZonaComun,
  FotoZonaComun 
} from "../../../../api/zonaComunService";

interface GaleriaImagenesModalProps {
  isOpen: boolean;
  zonaComunCodigo: string;
  onClose: (updated: boolean) => void;
}

export default function GaleriaImagenesModal({ 
  isOpen, 
  zonaComunCodigo,
  onClose 
}: GaleriaImagenesModalProps) {
  const [fotos, setFotos] = useState<FotoZonaComun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [isMarkingPrincipal, setIsMarkingPrincipal] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && zonaComunCodigo) {
      loadFotos();
      setImageErrors(new Set());
      setHasChanges(false); // Resetear cambios cuando se abre el modal
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, zonaComunCodigo]);

  const loadFotos = async () => {
    if (!zonaComunCodigo) return;

    try {
      setIsLoading(true);
      const payload = {
        "zona comun codigo": zonaComunCodigo,
      };

      const response = await obtenerFotosZonaComun(payload);

      if ((response.code === "1" || response.code === "-1") && response.object_) {
        setFotos(response.object_);
      } else {
        setFotos([]);
      }
    } catch (error) {
      console.error("Error al cargar fotos:", error);
      setFotos([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las imágenes",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10003';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10003';
          }
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEliminarFoto = async (fotoCodigo: number) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar imagen?",
      text: "Esta acción no se puede deshacer",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3d5a40",
      didOpen: (popup) => {
        (popup as HTMLElement).style.zIndex = '10003';
        const container = popup.closest('.swal2-container');
        if (container) {
          (container as HTMLElement).style.zIndex = '10003';
        }
      },
    });

    if (!result.isConfirmed) return;

    try {
      setIsDeleting(fotoCodigo);
      const payload = {
        codigo: fotoCodigo,
        "zona comun codigo": zonaComunCodigo,
      };

      const response = await eliminarFotoZonaComun(payload);

      if (response.code === "1" || response.code === "-1") {
        // Toast discreto en lugar de alert grande
        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          didOpen: (toast) => {
            (toast as HTMLElement).style.zIndex = '10003';
            const container = toast.closest('.swal2-container');
            if (container) {
              (container as HTMLElement).style.zIndex = '10003';
            }
          },
        });
        
        Toast.fire({
          icon: 'success',
          title: '✓ Imagen eliminada',
        });
        setHasChanges(true); // Marcar que hubo cambios
        loadFotos(); // Recargar lista sin cerrar el modal
      } else {
        throw new Error(response.message || "Error al eliminar la imagen");
      }
    } catch (error) {
      console.error("Error al eliminar foto:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al eliminar la imagen";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10003';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10003';
          }
        },
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleMarcarPrincipal = async (fotoCodigo: number) => {
    const result = await Swal.fire({
      icon: "question",
      title: "¿Marcar como principal?",
      text: "Esta imagen será la imagen principal de la zona común",
      showCancelButton: true,
      confirmButtonText: "Sí, marcar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3d5a40",
      cancelButtonColor: "#6b7280",
      didOpen: (popup) => {
        (popup as HTMLElement).style.zIndex = '10003';
        const container = popup.closest('.swal2-container');
        if (container) {
          (container as HTMLElement).style.zIndex = '10003';
        }
      },
    });

    if (!result.isConfirmed) return;

    try {
      setIsMarkingPrincipal(fotoCodigo);
      const payload = {
        codigo: fotoCodigo,
        "zona comun codigo": zonaComunCodigo,
      };

      const response = await marcarFotoPrincipal(payload);

      if (response.code === "1" || response.code === "-1") {
        Swal.fire({
          icon: "success",
          title: "Imagen marcada como principal",
          text: response.message || "La imagen se ha marcado como principal exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10003';
            const container = popup.closest('.swal2-container');
            if (container) {
              (container as HTMLElement).style.zIndex = '10003';
            }
          },
        });
        setHasChanges(true); // Marcar que hubo cambios
        loadFotos(); // Recargar lista sin cerrar el modal
      } else {
        throw new Error(response.message || "Error al marcar como principal");
      }
    } catch (error) {
      console.error("Error al marcar como principal:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al marcar como principal";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10003';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10003';
          }
        },
      });
    } finally {
      setIsMarkingPrincipal(null);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles: File[] = [];
      
      Array.from(files).forEach((file) => {
        if (file.size > 5 * 1024 * 1024) {
          Swal.fire({
            icon: "warning",
            title: "Archivo muy grande",
            text: `La imagen ${file.name} no debe superar los 5MB`,
            confirmButtonColor: "#3d5a40",
            didOpen: (popup) => {
              (popup as HTMLElement).style.zIndex = '10003';
              const container = popup.closest('.swal2-container');
              if (container) {
                (container as HTMLElement).style.zIndex = '10003';
              }
            },
          });
          return;
        }

        if (!file.type.startsWith("image/")) {
          Swal.fire({
            icon: "warning",
            title: "Formato inválido",
            text: `El archivo ${file.name} no es una imagen válida`,
            confirmButtonColor: "#3d5a40",
            didOpen: (popup) => {
              (popup as HTMLElement).style.zIndex = '10003';
              const container = popup.closest('.swal2-container');
              if (container) {
                (container as HTMLElement).style.zIndex = '10003';
              }
            },
          });
          return;
        }

        validFiles.push(file);
      });

      if (validFiles.length > 0) {
        handleUploadFotos(validFiles);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadFotos = async (fotosFiles: File[]) => {
    if (!zonaComunCodigo || fotosFiles.length === 0) return;

    try {
      setIsUploading(true);
      setUploadingCount(fotosFiles.length);
      
      // Guardar la foto principal actual antes de subir
      const fotoPrincipalActual = fotos.find(f => f.fzc_principal === 1);
      const codigoFotoPrincipal = fotoPrincipalActual?.fzc_codigo || null;
      
      const response = await insertarFotosZonaComun(zonaComunCodigo, fotosFiles);

      if (response.code === "1" || response.code === 1) {
        // Recargar lista primero
        const payload = {
          "zona comun codigo": zonaComunCodigo,
        };
        const fotosResponse = await obtenerFotosZonaComun(payload);
        
        if ((fotosResponse.code === "1" || fotosResponse.code === "-1") && fotosResponse.object_) {
          setFotos(fotosResponse.object_);
          
          // Verificar si la foto principal cambió y restaurarla si es necesario
          if (codigoFotoPrincipal) {
            const fotosActualizadas = fotosResponse.object_;
            const fotoPrincipalSigueSiendoPrincipal = fotosActualizadas.some(
              f => f.fzc_codigo === codigoFotoPrincipal && f.fzc_principal === 1
            );
            
            // Si la foto principal cambió, restaurarla
            if (!fotoPrincipalSigueSiendoPrincipal) {
              try {
                await marcarFotoPrincipal({
                  codigo: codigoFotoPrincipal,
                  "zona comun codigo": zonaComunCodigo,
                });
                // Recargar nuevamente después de restaurar
                const fotosResponseFinal = await obtenerFotosZonaComun(payload);
                if ((fotosResponseFinal.code === "1" || fotosResponseFinal.code === "-1") && fotosResponseFinal.object_) {
                  setFotos(fotosResponseFinal.object_);
                }
              } catch (error) {
                console.error("Error al restaurar foto principal:", error);
              }
            }
          }
        }
        
        // Toast discreto en lugar de alert grande
        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          didOpen: (toast) => {
            (toast as HTMLElement).style.zIndex = '10003';
            const container = toast.closest('.swal2-container');
            if (container) {
              (container as HTMLElement).style.zIndex = '10003';
            }
          },
        });
        
        Toast.fire({
          icon: 'success',
          title: `✓ ${fotosFiles.length} foto(s) agregada(s)`,
        });
        setHasChanges(true); // Marcar que hubo cambios
      } else {
        throw new Error(response.message || "Error al agregar las fotos");
      }
    } catch (error) {
      console.error("Error al agregar fotos:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al agregar las fotos";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10003';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10003';
          }
        },
      });
    } finally {
      setIsUploading(false);
      setUploadingCount(0);
    }
  };

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("La API de cámara no está disponible en este navegador.");
      }

      setShowCamera(true);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      setStream(mediaStream);
    } catch (error) {
      console.error("Error al acceder a la cámara:", error);
      setShowCamera(false);
      Swal.fire({
        icon: "error",
        title: "Error de cámara",
        text: "No se pudo acceder a la cámara. Verifica los permisos del navegador y que la cámara no esté siendo usada por otra aplicación.",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10003';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10003';
          }
        },
      });
    }
  };

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.error("No se pudo reproducir el video de la cámara:", err);
        });
      }
    }
  }, [showCamera, stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      if (context) {
        context.drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
            handleUploadFotos([file]);
            stopCamera();
          }
        }, 'image/jpeg', 0.8);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose(hasChanges);
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ImageIcon size={28} />
            Galería de Imágenes
          </h2>
          <button
            onClick={() => onClose(hasChanges)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 relative">
          {isUploading && (
            <div className="fixed top-20 right-6 bg-military-500 text-white px-4 py-3 rounded-lg shadow-xl z-50 flex items-center gap-3 transform transition-all">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="font-semibold">
                Subiendo {uploadingCount} foto{uploadingCount > 1 ? 's' : ''}...
              </span>
            </div>
          )}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-military-600 mb-4" />
              <p className="text-gray-600">Cargando imágenes...</p>
            </div>
          ) : fotos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <ImageIcon className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 text-center mb-4">
                No hay imágenes disponibles para esta zona común.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={startCamera}
                  disabled={isUploading}
                  className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center gap-2"
                >
                  <Camera size={18} />
                  Tomar Foto
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="file-upload-empty"
                  disabled={isUploading}
                />
                <label
                  htmlFor="file-upload-empty"
                  className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-military-500 text-military-600 rounded-lg cursor-pointer hover:bg-military-50 transition-all font-medium ${
                    isUploading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Upload size={18} />
                  Seleccionar Imágenes
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {fotos.length > 0 && (
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-600">
                    Total: <span className="font-semibold text-military-600">{fotos.length}</span> foto{fotos.length > 1 ? 's' : ''}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={startCamera}
                      disabled={isUploading}
                      variant="outline"
                      size="sm"
                      className="border-military-500 text-military-600 hover:bg-military-50"
                    >
                      <Camera size={16} className="mr-2" />
                      Tomar Foto
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      className="hidden"
                      id="file-upload-existing"
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="file-upload-existing"
                      className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 border-military-500 text-military-600 rounded-lg cursor-pointer hover:bg-military-50 transition-all text-sm font-semibold ${
                        isUploading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={16} />
                      Agregar Más
                    </label>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {fotos.map((foto) => (
                <div
                  key={foto.fzc_codigo}
                  className="relative group bg-white rounded-xl overflow-hidden border-2 border-gray-200 hover:border-military-500 transition-all shadow-md hover:shadow-xl"
                >
                  {foto.fzc_principal === 1 && (
                    <div className="absolute top-3 left-3 z-20 bg-gradient-to-r from-military-500 to-military-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg">
                      <Star size={14} fill="currentColor" />
                      Principal
                    </div>
                  )}

                  <div className="w-full h-72 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                    {foto.fzc_ruta_foto && !imageErrors.has(foto.fzc_codigo) ? (
                      <img
                        src={foto.fzc_ruta_foto}
                        alt={`Imagen ${foto.fzc_codigo}`}
                        className="max-w-full max-h-full object-contain transition-transform duration-300"
                        onError={() => {
                          setImageErrors((prev) => new Set(prev).add(foto.fzc_codigo));
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400 p-6">
                        <div className="bg-gray-200 rounded-full p-4 mb-3">
                          <ImageIcon size={40} />
                        </div>
                        <span className="text-sm font-medium">Sin imagen</span>
                      </div>
                    )}
                  </div>

                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 z-10">
                    {foto.fzc_principal !== 1 && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarcarPrincipal(foto.fzc_codigo);
                        }}
                        disabled={isMarkingPrincipal === foto.fzc_codigo}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white shadow-lg font-semibold px-4 py-2"
                        size="sm"
                      >
                        {isMarkingPrincipal === foto.fzc_codigo ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Star size={16} className="mr-2" />
                            Principal
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEliminarFoto(foto.fzc_codigo);
                      }}
                      disabled={isDeleting === foto.fzc_codigo}
                      className="bg-red-500 hover:bg-red-600 text-white shadow-lg font-semibold px-4 py-2"
                      size="sm"
                    >
                      {isDeleting === foto.fzc_codigo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 size={16} className="mr-2" />
                          Eliminar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t pt-4 px-6 pb-6">
          <div className="flex justify-between items-center">
            <div className="flex gap-3">
              <Button
                onClick={startCamera}
                disabled={isUploading}
                variant="outline"
                className="border-2 border-military-500 text-military-600 hover:bg-military-50 font-semibold flex items-center gap-2"
              >
                <Camera size={18} />
                <span className="hidden sm:inline">Tomar Foto</span>
                <span className="sm:hidden">Foto</span>
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className="hidden"
                id="file-upload-footer"
                disabled={isUploading}
              />
              <label
                htmlFor="file-upload-footer"
                className={`inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-military-500 text-military-600 rounded-lg cursor-pointer hover:bg-military-50 transition-all font-semibold ${
                  isUploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">Subiendo...</span>
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    <span className="hidden sm:inline">Agregar Fotos</span>
                    <span className="sm:hidden">Agregar</span>
                  </>
                )}
              </label>
            </div>
            <Button
              onClick={() => onClose(hasChanges)}
              className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold"
            >
              Cerrar
            </Button>
          </div>
        </div>
      </div>

      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-military-600">
                Tomar Foto
              </h3>
              <button
                onClick={stopCamera}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-auto max-h-[60vh]"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={capturePhoto}
                  className="bg-military-500 hover:bg-military-600 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Capturar Foto
                </Button>
                <Button
                  onClick={stopCamera}
                  variant="outline"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

